import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bPaymentReciptApi from 'api/b2b/PaymentRecipt'
import B2bTDPAPI from 'api/b2b/TdpApi'
import OrbitSession from 'hooks/useCookies'
import useDebounce from 'hooks/useDebounce'

import PaymentSearchTable from './PaymentSearchList'

interface Company {
  tenant_id: string
  company_name: string
  id: string
}

interface TenantData {
  data: Company[]
}

interface CompanyOption {
  label: string
  value: string
  companyId: string
}

export type PaymentReceiptTableDataProps = {
  receipt_number: string
  company_name: string
  receipt_date: string
  mode_of_payment: string
  outstanding_dues: string
  particulars: string
  receipt_currency: string
  receipt_amount: string
  created_by: string
  created_on: string
  action: string
}

export default function PaymentReceiptSearchForm() {
  const [companySearchTerm, setCompanySearchTerm] = useState('')
  const [tableData, setTableData] = useState<PaymentReceiptTableDataProps[]>([])
  const debouncedSearchTerm = useDebounce(companySearchTerm, 500) // 500ms debounce

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      receiptNumber: '',
      companyName: null,
      receiptDate: null,
      modeOfPayment: '',
      currency: ''
    }
  })

  // const companyNameInput = watch('companyName')

  // Query for fetching companies based on search term
  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isError: tenantFetchError
    // error: tenantError
  } = useQuery<TenantData>(
    ['tenant-search', debouncedSearchTerm],
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bTDPAPI.getTDPDetails(token, {
        company_name: debouncedSearchTerm
      })
      return data
    },
    {
      enabled: debouncedSearchTerm.length >= 2, // Only search when user types at least 2 characters
      staleTime: 2 * 60 * 1000, // Cache for 2 minutes
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
      refetchOnWindowFocus: false,
      onError: (error) => {
        console.error('Failed to fetch companies:', error)
      }
    }
  )

  const {
    mutate: searchPaymentRecipt,
    isLoading: statementLoading
    // error: statementFetchError,
    // data: statementData
  } = useMutation({
    mutationFn: async (params: Record<string, string>) => {
      const token = OrbitSession.get('faberToken')
      return await B2bPaymentReciptApi.getPaymentRecipt(token, params)
    },
    onSuccess: (data) => {
      if (data.data != null && Array.isArray(data.data)) setTableData(data.data)
      else {
        setTableData([])
        toast.info('No data found', { autoClose: 3000, hideProgressBar: false })
      }
    },
    onError: (error) => {
      console.error('Failed to fetch customer statement:', error)
    }
  })

  const onSubmit = (data) => {
    const cleanedData: Record<string, string> = {
      company_id: data.companyName?.companyId || ''
    }
    // Only include dates if they have values
    // TODO:Can we think of something better for this if block
    if (data.currency) {
      cleanedData.currency = data.currency
    }
    if (data.modeOfPayment) {
      cleanedData.mode_of_payment = data.modeOfPayment
    }
    if (data.receiptDate) {
      cleanedData.receipt_date = data.receiptDate
    }
    if (data.receiptNumber) {
      cleanedData.receipt_number = data.receiptNumber
    }
    searchPaymentRecipt(cleanedData)
  }

  const onReset = () => {
    reset()
    setTableData([])
  }
  const navigate = useNavigate()

  // Memoized company options
  const companyOptions = useMemo((): CompanyOption[] => {
    if (!tenantData || !Array.isArray(tenantData)) return []

    return tenantData.map((company: Company) => ({
      label: company.company_name || '',
      value: company.company_name || '',
      companyId: company.id || ''
    }))
  }, [tenantData])

  // Handle input change for company search
  const handleCompanyInputChange = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setCompanySearchTerm(value)
    },
    []
  )

  return (
    <Box sx={{ p: 3 }}>
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          backgroundColor: '#fff',
          p: 3,
          borderRadius: 2,
          boxShadow: 1,
          mb: 3
        }}
      >
        <Grid container spacing={3}>
          {/* Row 1 */}
          <Grid item xs={12} md={4}>
            <Controller
              name='receiptNumber'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Receipt Number'
                  size='small'
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            {/* Company Name with Debounced Search */}
            <Controller
              name='companyName'
              control={control}
              rules={{
                required: { value: true, message: 'Company name is required' }
              }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  {...field}
                  options={companyOptions}
                  loading={isTenantLoading}
                  getOptionLabel={(option: CompanyOption | null) =>
                    option?.label || ''
                  }
                  isOptionEqualToValue={(
                    option: CompanyOption,
                    value: CompanyOption | null
                  ) => option?.value === value?.value}
                  onChange={(_, data: CompanyOption | null) =>
                    field.onChange(data)
                  }
                  onInputChange={handleCompanyInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Company Name *'
                      variant='outlined'
                      fullWidth
                      size='small'
                      error={!!error}
                      helperText={
                        error?.message ||
                        (debouncedSearchTerm.length > 0 &&
                        debouncedSearchTerm.length < 2
                          ? 'Type at least 2 characters to search'
                          : '')
                      }
                      placeholder='Type company name to search...'
                      InputProps={{
                        ...params.InputProps
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component='li' {...props}>
                      <Box>
                        <Typography variant='body1'>{option.label}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          ID: {option.companyId}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText={
                    debouncedSearchTerm.length < 2
                      ? 'Type at least 2 characters to search'
                      : isTenantLoading
                      ? 'Searching...'
                      : tenantFetchError
                      ? 'Error loading companies'
                      : 'No companies found'
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='receiptDate'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type='date'
                  label='Receipt Date'
                  InputLabelProps={{ shrink: true }}
                  variant='outlined'
                  fullWidth
                  size='small'
                />
              )}
            />
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} md={4}>
            <Controller
              name='modeOfPayment'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label='Mode of Payment'
                  size='small'
                  fullWidth
                >
                  <MenuItem value=''>Select</MenuItem>
                  <MenuItem value='Cash'>Cash</MenuItem>
                  <MenuItem value='Bank Transfer'>Bank Transfer</MenuItem>
                  <MenuItem value='Credit Card'>Credit Card</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name='currency'
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label='Currency'
                  size='small'
                  fullWidth
                >
                  <MenuItem value=''>Select</MenuItem>
                  <MenuItem value='USD'>USD</MenuItem>
                  <MenuItem value='EUR'>EUR</MenuItem>
                  <MenuItem value='INR'>INR</MenuItem>
                </TextField>
              )}
            />
          </Grid>

          {/* Empty grid item to maintain alignment */}
          <Grid item xs={12} md={4}></Grid>

          {/* Buttons - Full width row */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-start',
                pt: 1
              }}
            >
              <Button
                variant='contained'
                color='primary'
                type='submit'
                disabled={statementLoading}
                sx={{ textTransform: 'none', minWidth: 120 }}
              >
                {statementLoading ? <CircularProgress size={20} /> : 'Search'}
              </Button>
              <Button
                variant='outlined'
                color='primary'
                type='button'
                onClick={onReset}
                sx={{ textTransform: 'none', minWidth: 120 }}
              >
                Reset
              </Button>
              <Button
                variant='outlined'
                color='primary'
                type='button'
                onClick={() => navigate('/hq/b2b/payments/add')}
                sx={{ textTransform: 'none' }}
              >
                Add Payment Receipt
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box>
        <PaymentSearchTable data={tableData} />
      </Box>
    </Box>
  )
}
